import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { CompanyType } from '@europrocurement/l2d-modules/modules/PrescriberClient/hooks/useSirenSearch';
import { useTiersService } from '@europrocurement/l2d-domain';
import { enqueueSnackbar } from 'notistack';
import PrescripteurFormContext, {
    CspOption,
    PaysOption,
    PortefeuilleOption,
    Step1Data,
    Step2Data,
    Step3Data,
} from './PrescripteurFormContext';
import { CIVILITE_OPTIONS } from '../components/CreatePrescripteur/contactTypes';

const PrescripteurFormProvider: React.FunctionComponent<PropsWithChildren> = function ({
    children,
}) {
    const [step1, setStep1] = useState<Step1Data | null>(null);
    const [step2, setStep2] = useState<Step2Data | null>(null);
    const [step3, setStep3] = useState<Step3Data | null>(null);
    const [rneData, setRneData] = useState<CompanyType | null>(null);

    const [walletOptions, setWalletOptions] = useState<PortefeuilleOption[]>([]);
    const [cspOptions, setCspOptions] = useState<CspOption[]>([]);
    const [paysOptions, setPaysOptions] = useState<PaysOption[]>([]);
    const [optionsLoading, setOptionsLoading] = useState(false);

    const step2Ref = useRef<Step2Data | null>(null);
    step2Ref.current = step2;

    const { paysApi, portefeuilleApi, categorieSocioProfessionnelleApi } = useTiersService();

    useEffect(() => {
        if (!rneData || !(rneData.address || rneData.postalCode || rneData.city)) return;
        if (step2Ref.current?.rue) return;

        const fullAddress = [
            rneData.address,
            rneData.addressComplement,
            rneData.postalCode,
            rneData.city,
        ]
            .filter(Boolean)
            .join(', ');

        setStep2({
            fullAddress,
            rue: rneData.address || '',
            adresseComplement: rneData.addressComplement || '',
            codePostal: rneData.postalCode || '',
            ville: rneData.city || '',
            pays: rneData.countryCode || '',
            portefeuille: step2Ref.current?.portefeuille ?? null,
            csp: step2Ref.current?.csp ?? null,
        });
    }, [rneData]);

    useEffect(() => {
        if (!rneData || !(rneData.firstName || rneData.lastName)) return;

        const civiliteOption =
            CIVILITE_OPTIONS.find((o) => o.id === rneData.civilite) ?? CIVILITE_OPTIONS[0];

        setStep3({
            contacts: [
                {
                    civilite: civiliteOption,
                    nomContact: rneData.lastName || '',
                    prenomContact: rneData.firstName || '',
                    email: '',
                    telephone: '',
                },
            ],
        });
    }, [rneData]);

    useEffect(() => {
        setOptionsLoading(true);

        Promise.all([
            portefeuilleApi.getListePortefeuilleCategorieSocioProfessionnelleCollection({}),
            categorieSocioProfessionnelleApi.getListeCspCategorieSocioProfessionnelleCollection({}),
            paysApi.getListePaysTblPaysCollection({ itemsPerPage: 500 }),
        ])
            .then(([walletRes, cspRes, paysRes]) => {
                setWalletOptions(walletRes.data['hydra:member'] ?? []);
                setCspOptions(cspRes.data['hydra:member'] ?? []);
                setPaysOptions(paysRes.data['hydra:member'] ?? []);
            })
            .catch(() => {
                enqueueSnackbar('Erreur lors du chargement des options', { variant: 'error' });
            })
            .finally(() => {
                setOptionsLoading(false);
            });
    }, [portefeuilleApi, categorieSocioProfessionnelleApi, paysApi]);

    const value = useMemo(
        () => ({
            step1,
            setStep1,
            step2,
            setStep2,
            step3,
            setStep3,
            rneData,
            setRneData,
            walletOptions,
            cspOptions,
            paysOptions,
            optionsLoading,
        }),
        [step1, step2, step3, rneData, walletOptions, cspOptions, paysOptions, optionsLoading],
    );

    return (
        <PrescripteurFormContext.Provider value={value}>
            {children}
        </PrescripteurFormContext.Provider>
    );
};

export default PrescripteurFormProvider;
